import { Directive, AfterViewInit, ElementRef, Input, OnDestroy, inject } from '@angular/core'
import { BehaviorSubject, Subscription } from 'rxjs'

@Directive({
  selector: '[jmlAutoFocus]'
})
export class AutoFocusDirective implements AfterViewInit, OnDestroy {
  /**
   * Sets the focus state based on the provided value.
   *
   * The `jmlAutoFocus` input can accept both boolean and string types due to potential
   * variations in how data is bound in templates. The directive is designed to default to `true`
   * under the following conditions:
   *   - If the input is an empty string, undefined, null, or any other value that isn't explicitly boolean.
   *   - If the input is the boolean `true` or the string "true".
   *
   * Only the boolean `false` or the string "false" will set the focus state to `false`.
   * This approach allows for flexible usage of the directive across different contexts where
   * the type of the bound value might not be strictly controlled.
   *
   * Usage:
   *   - `<div [jmlAutoFocus]="true">` or `<div jmlAutoFocus>`: Activates focus.
   *   - `<div [jmlAutoFocus]="false">`: Deactivates focus.
   *   - `<div [jmlAutoFocus]="'false'">`: Deactivates focus using a string.
   *   - `<div>` without the attribute does not affect the focus state; defaults to active focus.
   *
   * @param value The input value can be a boolean or a string. The directive interprets
   *              non-boolean values as `true`, except the string "false".
   */
  @Input() set jmlAutoFocus(value: boolean | string) {
    if (value === 'true' || value === true) {
      this.$focusSubject.next(true)
    } else if (value === 'false' || value === false) {
      this.$focusSubject.next(false)
    } else {
      this.$focusSubject.next(true) // Default if any other string or unexpected type
    }
  }
  private $focusSubject = new BehaviorSubject<boolean>(true)
  setFocus$ = this.$focusSubject.asObservable()

  private focusSubscription = Subscription.EMPTY // Initialize with empty subscription

  private el = inject(ElementRef)

  ngAfterViewInit(): void {
    this.focusSubscription = this.setFocus$.subscribe((setFocus) => {
      if (setFocus) {
        this.setFocus()
      }
    })
  }

  ngOnDestroy() {
    // Ensure to clean up the subscription when the component is destroyed
    this.focusSubscription.unsubscribe()
  }

  setFocus() {
    setTimeout(() => {
      if (this.el.nativeElement?.nodeName === 'INPUT') {
        this.el.nativeElement.focus()
      } else {
        this.el.nativeElement?.querySelector('input')?.focus()
      }
    }, 0)
  }
}
